<template>
  <div>
    <v-row class="px-6 mb-4" align="center">
      <v-col cols="4">
        <v-text-field
          v-model="searchInputValue"
          prepend-inner-icon="$magnify"
          outlined
          dense
          single-line
          hide-details="auto"
          :placeholder="$t('spedilink.myLinks.searchPlaceholder')"
          clearable
        />
      </v-col>
      <v-col class="d-flex justify-end align-center flex-wrap">
        <v-badge
          :content="privatePendingCount"
          :value="privatePendingCount > 0"
          color="error"
          overlap
          class="mr-2"
        >
          <v-btn
            color="primary"
            depressed
            outlined
            @click="privateNotificationsDialog = true"
          >
            {{ $t('spedilink.notifications.sendButton') }}
          </v-btn>
        </v-badge>
        <v-badge
          v-if="isManagerOrOwner"
          :content="companyPendingCount"
          :value="companyPendingCount > 0"
          color="error"
          overlap
          class="mr-2"
        >
          <v-btn
            color="primary"
            depressed
            outlined
            @click="companyNotificationsDialog = true"
          >
            {{ $t('spedilink.notifications.sendButtonCompany') }}
          </v-btn>
        </v-badge>
        <v-btn color="primary" depressed @click="$emit('create')">
          {{ $t('spedilink.myLinks.addLink') }}
        </v-btn>
        <v-btn color="primary" depressed class="ml-2" @click="goToAddFreight">
          {{ $t('spedilink.myLinks.addCargo') }}
        </v-btn>
        <v-btn color="primary" depressed class="ml-2" @click="goToAddVehicle">
          {{ $t('spedilink.myLinks.addVehicle') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-data-table
      :items="links"
      :loading="loading"
      :headers="headers"
      hide-default-footer
      :page="dataTableOptions.page"
      :items-per-page="dataTableOptions.itemsPerPage"
      @update:options="updateOptions"
    >
      <template v-slot:[`item.name`]="{ item }">
        <span v-if="item.isPublicLink">{{ $t('spedilink.links.publicLink') }}</span>
        <span v-else>{{ item.name }}</span>
      </template>

      <template v-slot:[`item.description`]="{ item }">
        <span v-if="item.isPublicLink">—</span>
        <span v-else>{{ item.description }}</span>
      </template>

      <template v-slot:[`item.userCount`]="{ item }">
        <span v-if="item.isPublicLink">—</span>
        <span v-else>{{ item.userCount }}</span>
      </template>

      <template v-slot:[`item.groupCount`]="{ item }">
        <span v-if="item.isPublicLink">—</span>
        <span v-else>{{ item.groupCount }}</span>
      </template>

      <template v-slot:[`item.createdAt`]="{ item }">
        <span v-if="item.id === GUID_EMPTY || item.isPublicLink">—</span>
        <span v-else>{{ item.createdAt | dateFormat }}</span>
      </template>

      <template v-slot:[`item.open`]="{ item }">
        <div class="d-flex align-center">
          <v-btn
            small
            color="primary"
            depressed
            @click="openList(item)"
          >
            {{ $t('spedilink.links.open') }}
          </v-btn>
          <v-btn
            v-if="!item.isPublicLink"
            icon
            class="ml-2"
            @click="openInterests(item)"
          >  <v-badge
              :content="getInterestsMetaForDisplay(item).total"
              :value="getInterestsMetaForDisplay(item).total > 0"
              :color="getInterestsMetaForDisplay(item).newTotal > 0 ? 'error' : 'error'"
              overlap
            >
              <v-icon :color="getInterestsMetaForDisplay(item).newTotal > 0 ? 'error' : 'grey lighten-1'">$interestCount</v-icon>
            </v-badge>
          </v-btn>
        </div>
      </template>

      <template v-slot:[`item.owner`]="{ item }">
        <span v-if="item.id === GUID_EMPTY || item.isPublicLink">—</span>
        <span
          v-else-if="item.companyShare"
          class="shared-owner-badge"
        >
          {{ formatOwnerName(item) }}
        </span>
        <span v-else>{{ formatOwnerName(item) }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-menu
          v-if="item.id !== GUID_EMPTY && !item.isPublicLink"
          bottom
          left
        >
          <template v-slot:activator="{ on, attrs }">
            <v-btn icon v-bind="attrs" v-on="on">
              <v-icon>mdi-dots-vertical</v-icon>
            </v-btn>
          </template>
          <v-list>
            <v-list-item v-if="item.isOwner" link @click="openEditDialog(item, 'edit')">
              <v-list-item-title>{{ $t('spedilink.links.edit') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="item.isOwner" link @click="openDeleteDialog(item)">
              <v-list-item-title class="error--text">{{ $t('spedilink.links.delete') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!item.isOwner && item.canManageSharing" link @click="openEditDialog(item, 'manageSharing')">
              <v-list-item-title>{{ $t('spedilink.links.manageSharing') }}</v-list-item-title>
            </v-list-item>
            <v-list-item v-if="!item.isOwner && !item.canManageSharing" link @click="openEditDialog(item, 'view')">
              <v-list-item-title>{{ $t('spedilink.links.view') }}</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </template>

      <template v-slot:footer v-if="pageCount > 1">
        <v-pagination v-model="dataTableOptions.page" :length="pageCount" />
      </template>
    </v-data-table>

    <EditSpedilinkLinkDialog
      v-model="editDialog"
      :link-id="selectedLink.id"
      :mode="editLinkMode"
      @close="editDialog = false"
      @link-updated="onLinkUpdated()"
    />

    <DeleteSpedilinkLinkDialog
      v-model="deleteDialog"
      :link="selectedLink"
      @close="deleteDialog = false"
      @link-deleted="onLinkDeleted()"
    />

    <SpedilinkPendingNotificationsDialog
      v-model="privateNotificationsDialog"
      :scope="NOTIFICATION_SCOPE.PRIVATE"
      @changed="loadPendingCounts()"
      @closed="loadPendingCounts()"
    />
    <SpedilinkPendingNotificationsDialog
      v-if="isManagerOrOwner"
      v-model="companyNotificationsDialog"
      :scope="NOTIFICATION_SCOPE.COMPANY"
      @changed="loadPendingCounts()"
      @closed="loadPendingCounts()"
    />

    <v-dialog v-model="interestsDialog" max-width="900" scrollable>
      <v-card>
        <v-card-title>{{ $t('offerDetails.interests') }}</v-card-title>
        <v-card-text>
          <SpedilinkInterestsPanel :interests="selectedInterests" :show-archive-actions="true" @interest-archived="onInterestArchived" />
        </v-card-text>
        <v-card-actions class="justify-end">
          <v-btn color="primary" depressed @click="interestsDialog = false">
            {{ $t('common.close') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Service from '@/services'
import EditSpedilinkLinkDialog from '@/views/Spedilink/dialogs/EditSpedilinkLinkDialog'
import DeleteSpedilinkLinkDialog from '@/views/Spedilink/dialogs/DeleteSpedilinkLinkDialog'
import SpedilinkInterestsPanel from '@/views/Market/dialogs/panels/SpedilinkInterestsPanel'
import SpedilinkPendingNotificationsDialog from '@/views/Spedilink/dialogs/SpedilinkPendingNotificationsDialog.vue'
import { GUID_EMPTY, SPEDILINK_LINK_TYPES, SPEDILINK_ROUTE_NAMES } from '@/views/Spedilink/spedilink-constants'

const NOTIFICATION_SCOPE = {
  PRIVATE: 0,
  COMPANY: 1
}

export default {
  name: 'SpedilinkLinksTable',
  components: {
    EditSpedilinkLinkDialog,
    DeleteSpedilinkLinkDialog,
    SpedilinkInterestsPanel,
    SpedilinkPendingNotificationsDialog
  },
  props: {
    type: {
      type: Number,
      required: true
    }
  },
  data () {
    return {
      GUID_EMPTY,
      NOTIFICATION_SCOPE,
      privatePendingCount: 0,
      companyPendingCount: 0,
      privateNotificationsDialog: false,
      companyNotificationsDialog: false,
      links: [],
      loading: false,
      pageCount: 1,
      searchInputValue: '',
      searchTimeout: null,
      dataTableOptions: {
        page: 1,
        itemsPerPage: 15
      },
      sortBy: null,
      sortDescending: false,
      selectedLink: {},
      editDialog: false,
      editLinkMode: 'edit',
      deleteDialog: false,
      interestsDialog: false,
      pendingMarkAllInterestsAsRead: false,
      selectedInterests: [],
      currentLinkForInterests: null,
      headers: [
        { text: this.$t('spedilink.links.name'), value: 'name' },
        { text: this.$t('spedilink.links.description'), value: 'description' },
        { text: this.$t('spedilink.links.userCount'), value: 'userCount' },
        { text: this.$t('spedilink.links.groupCount'), value: 'groupCount' },
        { text: this.$t('spedilink.links.owner'), value: 'owner', sortable: false },
        { text: this.$t('spedilink.links.createdAt'), value: 'createdAt' },
        { text: '', value: 'open', sortable: false },
        { text: '', align: 'end', width: '50', value: 'actions', sortable: false }
      ]
    }
  },
  async created () {
    await this.loadLinks()
    await this.loadPendingCounts()
  },
  watch: {
    searchInputValue () {
      this.dataTableOptions.page = 1
      clearTimeout(this.searchTimeout)
      this.searchTimeout = setTimeout(() => {
        this.loadLinks()
      }, 500)
    },
    'dataTableOptions.page' () {
      this.loadLinks()
    },
    interestsDialog (newVal, oldVal) {
      if (oldVal && !newVal) {
        this.onInterestsDialogClosed()
      }
    },
    spedilinkEventVersion () {
      this.onRealtimeEvent()
    }
  },
  computed: {
    spedilinkEventVersion () {
      return this.$store.getters['spedilink/eventVersion']
    },
    spedilinkLastEvent () {
      return this.$store.getters['spedilink/lastEvent']
    },
    isManagerOrOwner () {
      return this.$store.getters.isManagerOrOwner
    }
  },
  methods: {
    async onRealtimeEvent () {
      const event = this.spedilinkLastEvent
      console.log('eventlinks', event)
      if (!event) return

      // Refresh list on membership/list changes for this SpediLink type.
      if (event.eventName === 'ListAdded' || event.eventName === 'ListRemoved') {
        const eventType = event.type || event.payload?.type || event.payload?.Type
        if (!eventType || Number(eventType) === this.type) {
          await this.loadLinks()
        }
        return
      }

      if (event.eventName === 'LinkOrderAdded') {
        await this.loadPendingCounts()
        return
      }

      if (event.eventName === 'NewInterest') {
        // InterestCount is provided by backend; refresh list to update bell badge.
        await this.loadLinks()
        return
      }

      if (event.eventName === 'InterestsRead') {
        await this.loadLinks()
      }
    },
    async loadPendingCounts () {
      try {
        const { data: privateData } = await Service.get('spedilink/SpediLinkNotifications/GetList', { scope: NOTIFICATION_SCOPE.PRIVATE })
        this.privatePendingCount = Array.isArray(privateData) ? privateData.length : 0
      } catch (e) {
        this.privatePendingCount = 0
      }

      if (!this.isManagerOrOwner) {
        this.companyPendingCount = 0
        return
      }

      try {
        const { data: companyData } = await Service.get('spedilink/SpediLinkNotifications/GetList', { scope: NOTIFICATION_SCOPE.COMPANY })
        this.companyPendingCount = Array.isArray(companyData) ? companyData.length : 0
      } catch (e) {
        this.companyPendingCount = 0
      }
    },
    async loadLinks () {
      this.loading = true
      try {
        const params = {
          type: this.type,
          page: this.dataTableOptions.page,
          pageSize: this.dataTableOptions.itemsPerPage
        }
        if (this.searchInputValue) {
          params.search = this.searchInputValue
        }
        if (this.sortBy) {
          params.sortBy = this.sortBy
          params.sortDescending = this.sortDescending
        }
        const { data } = await Service.get('spedilink/SpediLinks/GetList', params)
        this.links = data.results
        this.pageCount = Math.ceil(data.count / this.dataTableOptions.itemsPerPage)
      } catch (e) {
      } finally {
        this.loading = false
      }
    },
    updateOptions (options) {
      if (options.sortBy.length > 0) {
        this.sortBy = options.sortBy[0]
        this.sortDescending = options.sortDesc[0] || false
      } else {
        this.sortBy = null
        this.sortDescending = false
      }
    },
    openList (item) {
      const routeName = this.type === SPEDILINK_LINK_TYPES.FREIGHT
        ? SPEDILINK_ROUTE_NAMES.CARGO_LIST
        : SPEDILINK_ROUTE_NAMES.VEHICLE_LIST
      const resolvedName = item.isPublicLink
        ? this.$t('spedilink.links.publicLink')
        : (item.name ?? item.Name)
      const route = { name: routeName, params: { id: item.id } }
      if (resolvedName != null && String(resolvedName).trim() !== '') {
        route.query = { linkName: String(resolvedName) }
      }
      if (item.isPublicLink) {
        route.query = { ...(route.query || {}), isPublicLink: '1' }
      }
      const href = this.$router.resolve(route).href
      window.open(href, '_blank')
    },
    goToAddFreight () {
      this.$router.push('/market/freight/add')
    },
    goToAddVehicle () {
      this.$router.push('/market/vehicle/add')
    },
    openEditDialog (link, mode = 'edit') {
      this.selectedLink = link
      this.editLinkMode = mode
      this.editDialog = true
    },
    formatOwnerName (link) {
      const first = link.ownerFirstName || ''
      const last = link.ownerLastName || ''
      return `${first} ${last}`.trim()
    },
    async onLinkUpdated () {
      this.editDialog = false
      await this.loadLinks()
    },
    openDeleteDialog (link) {
      this.selectedLink = link
      this.deleteDialog = true
    },
    async onLinkDeleted () {
      this.deleteDialog = false
      if (this.links.filter(l => l.id !== GUID_EMPTY).length === 1 && this.dataTableOptions.page > 1) {
        this.dataTableOptions.page -= 1
      } else {
        await this.loadLinks()
      }
    },
    getInterestsMeta (link) {
      // Prefer backend counter from SpediLinkVm, fallback to legacy fields.
      const raw = link.interests || link.Interests
      const totalFromArray = Array.isArray(raw) ? raw.length : 0
      const newFromArray = Array.isArray(raw) ? raw.filter(i => i.isNew || i.IsNew).length : 0

      const total = link.interestCount ?? link.InterestCount ?? link.interestsCount ?? link.InterestsCount ?? link.interestsCounter ?? totalFromArray
      const newTotal = link.newInterestsCount ?? link.NewInterestsCount ?? link.newInterestCount ?? newFromArray

      return {
        total: total || 0,
        newTotal: newTotal || 0
      }
    },
    getInterestsMetaForDisplay (link) {
      return this.getInterestsMeta(link)
    },
    mapInterest (i, fallbackKey) {
      return {
        id: i?.interestId || i?.InterestId || i?.id || fallbackKey,
        clientName: `${i?.clientFirstName || i?.ClientFirstName || ''} ${i?.clientLastName || i?.ClientLastName || ''}`.trim(),
        clientEmail: i?.clientEmail || i?.ClientEmail || '',
        clientPhone: i?.clientPhone || i?.ClientPhone || '',
        loadingPlace: i?.loadingPlace || i?.LoadingPlace || '',
        unloadingPlace: i?.unloadingPlace || i?.UnloadingPlace || '',
        content: i?.content || i?.Content || '',
        createdAt: i?.createdAt || i?.CreatedAt,
        isOrderArchived: i?.isOrderArchived || i?.IsOrderArchived,
        orderId: i?.orderId || i?.OrderId,
        orderType: i?.orderType || i?.OrderType || '',
        isNew: i?.isNew || i?.IsNew || false
      }
    },
    async openInterests (link) {
      this.interestsDialog = true
      this.currentLinkForInterests = link
      this.selectedInterests = []
      this.pendingMarkAllInterestsAsRead = false
      try {
        const getInterestsParams = link?.id === GUID_EMPTY ? {} : { linkId: link.id }
        const { data } = await Service.get('spedilink/SpediLinks/GetInterests', getInterestsParams)
        const raw = data?.results || data?.Results || data
        const list = Array.isArray(raw) ? raw : []
        this.selectedInterests = list.map((i, idx) => this.mapInterest(i, `${link.id}-${idx}`))
      } catch (e) {
        this.selectedInterests = []
      }

      // Mark interests as read and refresh counters (silent failure is OK).
      try {
        const hasNew = this.selectedInterests.some(i => i?.isNew)
        if (hasNew) {
          if (link?.id === GUID_EMPTY) {
            this.pendingMarkAllInterestsAsRead = true
          } else {
            await Service.post('spedilink/SpediLinks/MarkInterestsAsRead', {
              linkId: link.id,
              linkType: this.type === SPEDILINK_LINK_TYPES.FREIGHT
                ? SPEDILINK_LINK_TYPES.FREIGHT
                : SPEDILINK_LINK_TYPES.VEHICLE
            })
            await this.loadLinks()
          }
        }
      } catch (e) {
      }
    },
    async onInterestsDialogClosed () {
      if (!this.pendingMarkAllInterestsAsRead) {
        return
      }

      this.pendingMarkAllInterestsAsRead = false
      try {
        await Service.post('spedilink/SpediLinks/MarkInterestsAsRead', {
          linkId: null,
          linkType: this.type === SPEDILINK_LINK_TYPES.FREIGHT
            ? SPEDILINK_LINK_TYPES.FREIGHT
            : SPEDILINK_LINK_TYPES.VEHICLE
        })
        await this.loadLinks()
      } catch (e) {
      }
    },
    async onInterestArchived (interest) {
      // Reload interests list from API to reflect all changes for the same order
      if (this.currentLinkForInterests) {
        try {
          const link = this.currentLinkForInterests
          const getInterestsParams = link?.id === GUID_EMPTY ? {} : { linkId: link.id }
          const { data } = await Service.get('spedilink/SpediLinks/GetInterests', getInterestsParams)
          const raw = data?.results || data?.Results || data
          const list = Array.isArray(raw) ? raw : []
          this.selectedInterests = list.map((i, idx) => this.mapInterest(i, `${link.id}-${idx}`))
        } catch (e) {
          console.error('Error reloading interests:', e)
        }
      }
      await this.loadLinks()
    }
  }
}
</script>

<style scoped>
.shared-owner-badge {
  display: inline-block;
  padding: 2px 8px;
  border-radius: 4px;
  background-color: #4caf50;
  color: #fff;
}
</style>
